import { Paper } from '@mui/material';
import { DataGridPro, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';

/** Replacement for the removed GridSortItem type from MUI X v8 */
interface GridSortItem {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}
import { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paginated } from '../../api/common';
import { sortDirection } from '../../api/generated';
import { getDataGridLocaleText } from '../../utils';

export type RecordingsTableData = Paginated<{ id: string }>;

export interface RecordingSorting extends GridSortItem {
  field: keyof { id: string };
  sort: sortDirection;
}

export interface RecordingsListProps {
  columns: GridColDef[];
  data: RecordingsTableData | null;
  selectedId?: string;
  onSelect: (recordingId: string | null) => void;
  pageSize: number;
  page?: number;
  onPageChange: (page: number) => void;
  onSortChange: (sort: RecordingSorting | null) => void;
  getRowClassName?: (params: any) => string;
}

export const RecordingsList = memo(function ({
  columns,
  data,
  onSelect,
  selectedId,
  pageSize,
  page,
  onPageChange,
  onSortChange,
  getRowClassName,
}: RecordingsListProps): ReactElement {
  const { t } = useTranslation();

  const [lastValidTotal, setLastValidTotal] = useState(0);

  useEffect(() => {
    if (data && Number.isInteger(data.total)) {
      setLastValidTotal(data.total);
    }
  }, [data]);

  const handleSortModelChange = useCallback(
    function (model: GridSortModel): void {
      onSortChange(model?.length ? (model[0] as RecordingSorting) : null);
    },
    [onSortChange]
  );

  const handleRowSelectionModelChange = useCallback(
    function (model: GridRowSelectionModel): void {
      const ids = model.type === 'include' ? Array.from(model.ids) : [];
      if (!ids.length) {
        onSelect(null);
        return;
      }

      const selected = ids[0];
      if (typeof selected === 'string') {
        onSelect(selected);
      } else {
        throw new Error('unexpected selection type');
      }
    },
    [onSelect]
  );

  const translatedColumns = columns.map((col) => ({
    ...col,
    headerName: t(col.headerName ?? ''),
  }));

  return (
    <Paper
      className='data-grid'
      elevation={0}
      sx={{
        border: 0,
        display: 'flex',
        flexGrow: 1,
        minHeight: 0,
        width: '100%',
      }}
    >
      <DataGridPro
        sx={{
          border: 0,
          boxShadow: 0,
          borderColor: 'white',
          '& .archived-row': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)',
              },
            },
          },
        }}
        localeText={getDataGridLocaleText()}
        columns={translatedColumns}
        rows={data?.data ?? []}
        loading={!data}
        pagination
        paginationMode='server'
        pageSizeOptions={[pageSize]}
        paginationModel={{ pageSize, page: page ?? 0 }}
        onPaginationModelChange={(model) => onPageChange(model.page)}
        rowCount={lastValidTotal}
        sortingMode='server'
        onSortModelChange={handleSortModelChange}
        sortingOrder={['asc', 'desc']}
        disableMultipleColumnsSorting
        filterMode='server'
        disableMultipleRowSelection
        disableColumnPinning
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu
        getRowClassName={getRowClassName}
        // selection
        onRowSelectionModelChange={handleRowSelectionModelChange}
        rowSelectionModel={
          selectedId ? { type: 'include', ids: new Set([selectedId]) } : { type: 'include', ids: new Set() }
        }
      />
    </Paper>
  );
});
